import _cloneDeep from "lodash/cloneDeep";
import _startCase from "lodash/startCase";
import { getMeasurementDimension } from '@kentik/ui-shared/query/measurements';
import { getDimensionDisplayLabel, isInterfaceIdDimension } from '@kentik/ui-shared/query/query';
import { getOperatorsForMeasurementDimension } from '@kentik/ui-shared/dictionary/v2/compilers/operators';
import { DEVICE_ID_DIMENSIONS, DEVICE_NAME_DIMENSION } from '@kentik/ui-shared/query/deviceDimensions';
function pickOperator(operators, preferLike) {
  const ordered = preferLike ? ['ILIKE', '='] : ['=', 'ILIKE'];
  return ordered.find(operator => operators?.[operator]) ?? '=';
}
function getDeviceScope(row, valueOf) {
  const idField = DEVICE_ID_DIMENSIONS.find(name => valueOf(name) !== undefined);
  const idValue = idField && valueOf(idField);
  const deviceName = row.dimensions[DEVICE_NAME_DIMENSION];
  return [...(idField && idValue !== undefined ? [{
    filterField: idField,
    operator: '=',
    filterValue: idValue
  }] : []), ...(deviceName ? [{
    filterField: DEVICE_NAME_DIMENSION,
    operator: '=',
    filterValue: deviceName
  }] : [])];
}
export function getRowFilterCandidates({
  query,
  measurement,
  row
}) {
  const {
    dimensions
  } = query;
  if (!dimensions.length) {
    return [];
  }
  const rawValues = row.id.split(' ---- ');
  const idDimensions = Object.keys(row.dimensions);
  if (rawValues.length > idDimensions.length) {
    return [];
  }
  const valueOf = name => rawValues[idDimensions.indexOf(name)];
  const deviceScope = getDeviceScope(row, valueOf);
  return dimensions.map(dimension => {
    const {
      name
    } = dimension;
    const info = getMeasurementDimension(measurement, name);
    const preferLike = !!dimension.cut;
    const rawValue = valueOf(name);
    return {
      dimension: name,
      label: getDimensionDisplayLabel(name, info?.Label || _startCase(name)),
      displayValue: row.dimensions[name] ?? rawValue ?? '',
      filterValue: rawValue === undefined || rawValue === '---' ? '' : rawValue,
      operator: pickOperator(getOperatorsForMeasurementDimension(info ?? undefined), preferLike),
      scopeFilters: deviceScope.length && isInterfaceIdDimension(name) ? deviceScope : undefined,
      unfilterableReason: info && rawValue !== undefined ? undefined : `Filtering isn't available for ${name}`
    };
  });
}
export function buildRowFilters(candidates) {
  const filters = candidates.flatMap(candidate => [{
    filterField: candidate.dimension,
    operator: candidate.operator,
    filterValue: candidate.filterValue
  }, ...(candidate.scopeFilters ?? [])]);
  return filters.filter((filter, index) => filters.findIndex(other => other.filterField === filter.filterField && other.operator === filter.operator && other.filterValue === filter.filterValue) === index);
}
function getDistinctFilterFields(group) {
  const {
    filters = [],
    filterGroups = []
  } = group;
  if (!filters.length && filterGroups.length) {
    const fields = getDistinctFilterFields(filterGroups[0]);
    const allMatch = filterGroups.every(nested => {
      const nestedFields = getDistinctFilterFields(nested);
      return fields.every(field => nestedFields.includes(field));
    });
    return allMatch ? fields : [];
  }
  if (filters.length && !filterGroups.length) {
    return Array.from(new Set(filters.map(filter => filter.filterField)));
  }
  return [];
}
function getGroupNot(group) {
  const {
    filters = [],
    filterGroups = []
  } = group;
  if (!filters.length && filterGroups.length) {
    const {
      not
    } = filterGroups[0];
    return filterGroups.every(nested => nested.not === not) ? not : undefined;
  }
  if (filters.length && !filterGroups.length) {
    return group.not;
  }
  return undefined;
}
export function addRowFilters(existing, filters, {
  include
}) {
  const fields = Array.from(new Set(filters.map(filter => filter.filterField)));
  const connector = include && fields.length === 1 ? 'Any' : 'All';
  const newGroup = {
    connector,
    not: !include,
    filters
  };
  const filterGroups = _cloneDeep(existing?.filterGroups ?? []);
  const match = filterGroups.find(group => {
    const groupFields = getDistinctFilterFields(group);
    return groupFields.length === fields.length && fields.every(field => groupFields.includes(field)) && getGroupNot(group) === !include;
  });
  if (!match) {
    filterGroups.push(newGroup);
  } else if (match.filters?.length && fields.length === 1) {
    match.connector = 'Any';
    match.filters.push(...filters);
  } else {
    if (match.filters?.length) {
      match.filterGroups = [{
        connector,
        not: match.not,
        filters: match.filters
      }];
      match.connector = match.not ? 'All' : 'Any';
      match.not = false;
      match.filters = [];
    }
    match.filterGroups?.push(newGroup);
  }
  const hasExclude = !include || filterGroups.some(group => group.not);
  return {
    connector: hasExclude ? 'All' : existing?.connector ?? 'All',
    filterGroups
  };
}